import React from 'react';
import 'src/assets/styles/react-table.scss';
import { useTable, useFilters, useSortBy, useRowSelect, useExpanded } from 'react-table';
import { Box, Icon, Popover, StatusIndicator } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';

type ComponentProps = {
    data: any[];
    columnDefinitions: any[];
    className: string;
    sortable?: boolean;
    showFilters?: boolean;
    showHeader?: boolean;
    highlightErrorCells?: boolean;
    isSearching?: boolean;
    isSearchingMessage?: string;
    emptyMessage?: string;
    onSortBy?: (e: Array<any>) => any;
    onFilter?: (e: Array<any>) => any;
    onRowBlur?: (...args: any[]) => any;
    onRowClicked?: (...args: any[]) => any;
    onExpandedChanged?: (e: any) => void;
    editableOptions?: {
        defaultColumn: any;
        onRowUpdated: any;
        skipPageReset: boolean;
        editorsData?: any;
        onCellUpdated?: any;
    };
    initialState?: any;
    allowMultipleRowSelection?: boolean;
    allowGlobalSelection?: boolean;
    alternateBackgroundColor?: boolean;
    alternateCount?: number;
    highlightSelected?: boolean;
    /** When true, the selection checkboxes (header select-all + per-row) are rendered disabled. */
    selectionDisabled?: boolean;
    /**
     * Controlled selection: when provided (with getRowKey), a row's checkbox is checked iff its
     * key is in this set — selection then survives data reloads. Rows whose key is '' (e.g. new
     * unsaved rows) render a disabled checkbox.
     */
    selectedRowKeys?: Set<string>;
    /** Derives the stable key for a row; required for controlled selection. */
    getRowKey?: (row: any) => string;
};

const doNothing = () => {};

const IndeterminateCheckBox = React.forwardRef<{ indeterminate: any }, any>(({ ...rest }: any, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;
    const indeterminate = (rest as any).indeterminate;
    (rest as any).ref = resolvedRef;
    React.useEffect(() => {
        (resolvedRef as any).current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
        <div className="cell-text checkbox-container">
            <input type="checkbox" {...rest} />
        </div>
    );
});

export function TPERowSelectableReactTable(props: ComponentProps) {
    const {
        className,
        data,
        columnDefinitions,
        sortable = true,
        showFilters = false,
        showHeader = true,
        highlightErrorCells = false,
        isSearching = false,
        onSortBy = doNothing,
        onFilter = doNothing,
        emptyMessage = 'No results',
        editableOptions,
        isSearchingMessage = 'Searching...',
        onRowBlur,
        onRowClicked = doNothing,
        onExpandedChanged = doNothing,
        allowMultipleRowSelection = false,
        allowGlobalSelection = false,
        alternateBackgroundColor = false,
        alternateCount = 1,
        highlightSelected = true,
        selectionDisabled = false,
        selectedRowKeys,
        getRowKey,
    } = props;

    const isControlled = selectedRowKeys != null && getRowKey != null;

    // The selection column's Header/Cell renderers are created once by the visibleColumns hook
    // (react-table memoizes on column identity), so they must read changing props through refs —
    // a closure would keep the first render's values (empty key set, initial disabled flag) forever.
    const selectedRowKeysRef = React.useRef(selectedRowKeys);
    selectedRowKeysRef.current = selectedRowKeys;
    const selectionDisabledRef = React.useRef(selectionDisabled);
    selectionDisabledRef.current = selectionDisabled;

    // Use the state and functions returned from useTable to build your UI
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = useTable(
        {
            columns: columnDefinitions,
            data,
            manualSortBy: true,
            manualFilters: true,
            disableMultiSort: true,
            // Reset index-based row selection on data change; keep filter/sort so a server result
            // doesn't wipe the active inputs.
            autoResetSelectedRows: true,
            autoResetFilters: false,
            autoResetSortBy: false,
            defaultColumn: editableOptions?.defaultColumn ? editableOptions.defaultColumn : {},
            onRowUpdated: editableOptions?.onRowUpdated ? editableOptions.onRowUpdated : doNothing,
            onCellUpdated: editableOptions?.onCellUpdated ? editableOptions.onCellUpdated : doNothing,
            autoResetPage: editableOptions?.skipPageReset != null ? !editableOptions.skipPageReset : true,
            editorsData: editableOptions ? editableOptions.editorsData : null,
        } as any,
        useFilters,
        useSortBy,
        useExpanded,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: 'selection',
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({ getToggleAllRowsSelectedProps }: any) => {
                        if (allowGlobalSelection && allowMultipleRowSelection) {
                            const toggleProps = getToggleAllRowsSelectedProps();
                            if (isControlled) {
                                // Derive select-all state from the controlled key set over the
                                // selectable (keyed) visible rows.
                                const currentKeys = selectedRowKeysRef.current!;
                                const keyedRows = rows.filter((r: any) => getRowKey!(r.original) !== '');
                                const selectedCount = keyedRows.filter(
                                    (r: any) => currentKeys.has(getRowKey!(r.original))
                                ).length;
                                const allSelected = keyedRows.length > 0 && selectedCount === keyedRows.length;
                                return (
                                    <div>
                                        <IndeterminateCheckBox
                                            checked={allSelected}
                                            indeterminate={selectedCount > 0 && !allSelected}
                                            disabled={selectionDisabledRef.current}
                                            onChange={doNothing}
                                            onClick={(e: any) => {
                                                if (selectionDisabledRef.current) {
                                                    return;
                                                }
                                                rows.forEach((row: any, index: number) => {
                                                    onRowClicked(e.target.checked, row.original, index);
                                                });
                                            }}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div>
                                    <IndeterminateCheckBox
                                        {...toggleProps}
                                        disabled={selectionDisabledRef.current}
                                        onClick={(e: any) => {
                                            if (selectionDisabledRef.current) {
                                                return;
                                            }
                                            // Call the original toggle handler
                                            if (toggleProps.onClick) {
                                                toggleProps.onClick(e);
                                            }
                                            // Also call onRowClicked for each row to select all cells
                                            rows.forEach((row: any, index: number) => {
                                                onRowClicked(e.target.checked, row.original, index);
                                            });
                                        }}
                                    />
                                </div>
                            );
                        }
                        return <div />;
                    },
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    sortable: false,
                    disableFilters: true,
                    suppressFiltering: true,
                    Cell: ({ row }: { row: any }) => {
                        if (allowMultipleRowSelection) {
                            if (isControlled) {
                                const rowKey = getRowKey!(row.original);
                                if (rowKey === '') {
                                    // Unkeyed rows (e.g. new unsaved) cannot join the selection.
                                    return (
                                        <div>
                                            <IndeterminateCheckBox checked={false} onChange={doNothing} disabled />
                                        </div>
                                    );
                                }
                                return (
                                    <div>
                                        <IndeterminateCheckBox
                                            checked={selectedRowKeysRef.current!.has(rowKey)}
                                            onChange={doNothing}
                                            onClick={(e: any) => {
                                                if (selectionDisabledRef.current) {
                                                    return;
                                                }
                                                onRowClicked(e.target.checked, row.original, row.index);
                                            }}
                                            disabled={selectionDisabledRef.current}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div>
                                    <IndeterminateCheckBox
                                        onClick={(e: any) => {
                                            if (selectionDisabledRef.current) {
                                                return;
                                            }
                                            onRowClicked(e.target.checked, row.original, row.index);
                                        }}
                                        {...row.getToggleRowSelectedProps()}
                                        disabled={selectionDisabledRef.current}
                                    />
                                </div>
                            );
                        }
                        // If there is no other checkbox selected
                        if (rows.filter((r: any) => r.isSelected).length < 1 || row.isSelected) {
                            return (
                                <div>
                                    <IndeterminateCheckBox
                                        onClick={(e: any) => onRowClicked(e.target.checked, row.original, row.index)}
                                        {...row.getToggleRowSelectedProps()}
                                    />
                                </div>
                            );
                        } else {
                            return (
                                <div>
                                    <IndeterminateCheckBox />
                                </div>
                            );
                        }
                    },
                },
                ...columns,
            ]);
        }
    );

    const { sortBy, filters } = state as any;
    React.useEffect(() => {
        onSortBy(sortBy);
    }, [sortBy]);
    React.useEffect(() => {
        onFilter(filters);
    }, [filters]);

    const getCellClass = (row: any, cell: any) => {
        return highlightErrorCells &&
            row.original.errors != null &&
            (row.original.errors as Map<string, string[]>).has(cell.column.id)
            ? 'errorCell'
            : row.original.editableFields != null && (row.original.editableFields as string[]).includes(cell.column.id)
              ? 'editableTd'
              : '';
    };

    const getRowClass = (index: number) => {
        if (alternateBackgroundColor) {
            const oneBasedIndex = index + 1;
            const toAlternate = alternateCount * 2;

            for (var i = 0; i < alternateCount; i++) {
                if ((oneBasedIndex + i) % toAlternate == 0) {
                    return 'greyRow';
                }
            }

            return '';
        }
    };

    // Render the UI for your table
    return (
        <div>
            <table className={className + CONSTANTS.ATP_CUSTOM_REACT_TABLE_CSS} {...getTableProps()}>
                {showHeader && (
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any) => {
                                    // suppressSorting / sortable:false are custom flags react-table doesn't
                                    // honor, so gate the toggle props too, not just the caret.
                                    const isSortableColumn = sortable && !column.suppressSorting && column.sortable !== false;
                                    return (
                                    <th {...column.getHeaderProps()} className={column.headerClass}>
                                        <div className="sorting-arrows" {...(isSortableColumn ? column.getSortByToggleProps() : {})}>
                                            {column.render('Header')}
                                            {isSortableColumn && (
                                                <div>
                                                    {column.isSorted ? (
                                                        column.isSortedDesc ? (
                                                            <Icon name="caret-down-filled" />
                                                        ) : (
                                                            <Icon name="caret-up-filled" />
                                                        )
                                                    ) : (
                                                        <Icon name="caret-down" />
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {showFilters ? (
                                            <div className="cell-filter">
                                                {column.canFilter && !column.suppressFiltering ? (
                                                    column.hidePopover ? (
                                                        column.render('Filter')
                                                    ) : (
                                                        <Popover
                                                            position="bottom"
                                                            size="medium"
                                                            triggerType="custom"
                                                            dismissButton={false}
                                                            content={
                                                                <StatusIndicator type="warning">
                                                                    Search is case sensitive
                                                                </StatusIndicator>
                                                            }>
                                                            {column.render('Filter')}
                                                        </Popover>
                                                    )
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                )}
                {isSearching || rows == null || rows.length == 0 ? null : (
                    <tbody {...getTableBodyProps()}>
                        {rows.map((row: any, i) => {
                            prepareRow(row);

                            return (
                                <tr
                                    className={row.isSelected && highlightSelected ? 'selected-row' : getRowClass(i)}
                                    {...row.getRowProps()}>
                                    {(row.original as any).colspan ? (
                                        <td
                                            className="colspanRow"
                                            colSpan={row.cells.length}
                                            {...row.cells[0].getCellProps()}>
                                            {(row.original as any).content}
                                        </td>
                                    ) : (
                                        row.cells.map((cell: any) => {
                                            return (
                                                <td {...cell.getCellProps()} className={getCellClass(row, cell)}>
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                )}
            </table>
            {isSearching ? (
                <Box textAlign="center" padding="l">
                    <StatusIndicator type="loading">{isSearchingMessage}</StatusIndicator>
                </Box>
            ) : null}
            {!isSearching && (rows == null || rows.length == 0) ? (
                <Box textAlign="center" padding="l" className="tpeTableEmptyMessage">
                    <StatusIndicator type="info">{emptyMessage}</StatusIndicator>
                </Box>
            ) : null}
        </div>
    );
}
